// src/features/etf-ranking/components/SymbolPopup.tsx
//
// Floating "quick overview" pop-over for a single ETF symbol. Opened by
// clicking a ticker in the universe table; fetches the latest close
// price + profile metrics from GET /api/v1/etf-ranking/quick-stats/<symbol>.
//
// Fixed-income sibling of features/stock-ranking/components/SymbolPopup.
// The ETF pop-over surfaces the richer profile payload: category,
// expense ratio, dividend yield, AUM and NAV (the equity version only
// shows last price + market cap).
//
// Design notes
// ------------
//   - Matches the suite's slate/emerald design language + full dark-mode.
//   - Self-contained data fetch via the shared `apiClient` (the codebase
//     uses axios + useEffect, not React Query). Cancellation-safe so
//     re-clicking a different symbol mid-flight never paints a stale row.
//   - Accessibility: role="dialog" + aria-modal, Escape-to-close,
//     backdrop-click-to-close (with stopPropagation on the card), and a
//     body scroll-lock while open.

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { fetchQuickStats, type QuickStats } from '../api';

// Compact $-suffixed AUM string ($40.0B / $980.0M). "N/A" for null /
// zero / non-finite values.
const formatAum = (value: number | null): string => {
    if (value == null || !Number.isFinite(value) || value <= 0) return 'N/A';
    if (value >= 1e12) return `$${(value / 1e12).toFixed(2)}T`;
    if (value >= 1e9) return `$${(value / 1e9).toFixed(2)}B`;
    if (value >= 1e6) return `$${(value / 1e6).toFixed(2)}M`;
    return `$${value.toLocaleString()}`;
};

const formatPrice = (value: number | null): string => {
    if (value == null || !Number.isFinite(value)) return 'N/A';
    return `$${value.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;
};

// Expense ratios + dividend yields arrive from the backend ALREADY as
// percent-numbers (FMP's expenseRatio: 0.43 == 0.43%; dividend_yield is
// _trailing_yield = (div / px) * 100, e.g. 5.88 == 5.88%). Append a
// trailing "%" WITHOUT re-scaling; "N/A" on null.
const formatPercent = (value: number | null): string => {
    if (value == null || !Number.isFinite(value)) return 'N/A';
    return `${value.toFixed(2)}%`;
};

interface SymbolPopupProps {
    symbol: string;
    onClose: () => void;
}

interface StatRow {
    label: string;
    value: string;
}

export const SymbolPopup = ({ symbol, onClose }: SymbolPopupProps) => {
    const navigate = useNavigate();
    const [data, setData] = useState<QuickStats | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Fetch on open / whenever the selected symbol changes. Cancellation
    // flag prevents a slow response for a previous symbol from clobbering
    // a newer one if the user clicks through several tickers quickly.
    useEffect(() => {
        let cancelled = false;
        setIsLoading(true);
        setError(null);
        setData(null);
        (async () => {
            try {
                const res = await fetchQuickStats(symbol);
                if (!cancelled) setData(res);
            } catch (err) {
                if (cancelled) return;
                // eslint-disable-next-line no-console
                console.error('Failed to load quick stats', err);
                setError('Unable to load data for this symbol.');
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [symbol]);

    // Escape-to-close + body scroll-lock while the modal is mounted.
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', onKey);
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', onKey);
            document.body.style.overflow = prevOverflow;
        };
    }, [onClose]);

    const rows: StatRow[] = data
        ? [
              { label: 'Last Price', value: formatPrice(data.last_price) },
              { label: 'NAV', value: formatPrice(data.nav) },
              {
                  label: 'Expense Ratio',
                  value: formatPercent(data.expense_ratio),
              },
              {
                  label: 'Dividend Yield',
                  value: formatPercent(data.dividend_yield),
              },
              {
                  label: 'AUM',
                  value: formatAum(data.assets_under_management),
              },
          ]
        : [];

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4"
            onClick={onClose}
            role="presentation"
        >
            <div
                className="relative w-full max-w-sm rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-6 shadow-2xl"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-label={`${symbol} quick overview`}
            >
                <button
                    type="button"
                    className="absolute top-4 right-4 flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-200 transition-colors"
                    onClick={onClose}
                    aria-label="Close"
                >
                    ✕
                </button>

                <h2 className="text-2xl font-bold text-slate-900 dark:text-white pr-8">
                    {data?.company_name ?? symbol}
                </h2>
                <p className="mt-0.5 text-sm font-medium text-green-600 dark:text-emerald-400">
                    {symbol}
                    {data?.category && (
                        <span className="ml-2 rounded-md bg-slate-100 dark:bg-slate-800 px-2 py-0.5 text-xs font-medium text-slate-500 dark:text-slate-400">
                            {data.category}
                        </span>
                    )}
                </p>
                <p className="mt-1 mb-5 text-xs uppercase tracking-wide text-slate-400 dark:text-slate-500">
                    Quick Overview
                </p>

                {isLoading && (
                    <div className="space-y-3" aria-busy="true">
                        <div className="h-10 animate-pulse rounded-lg bg-slate-100 dark:bg-slate-800" />
                        <div className="h-10 animate-pulse rounded-lg bg-slate-100 dark:bg-slate-800" />
                        <div className="h-10 animate-pulse rounded-lg bg-slate-100 dark:bg-slate-800" />
                    </div>
                )}

                {error && !isLoading && (
                    <div className="rounded-lg border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-900/20 px-3 py-2 text-sm text-red-700 dark:text-red-400">
                        {error}
                    </div>
                )}

                {data && !isLoading && !error && (
                    <dl className="space-y-1">
                        {rows.map((row, i) => (
                            <div
                                key={row.label}
                                className={`flex items-center justify-between py-3 ${
                                    i < rows.length - 1
                                        ? 'border-b border-slate-100 dark:border-slate-800'
                                        : ''
                                }`}
                            >
                                <dt className="text-sm text-slate-500 dark:text-slate-400">
                                    {row.label}
                                </dt>
                                <dd className="font-mono font-semibold text-slate-900 dark:text-white">
                                    {row.value}
                                </dd>
                            </div>
                        ))}
                    </dl>
                )}

                {/* Navigate to the full ETF deep-dive page for this symbol. */}
                <button
                    type="button"
                    onClick={() =>
                        navigate(
                            `/dashboard/etf-info-page?symbol=${encodeURIComponent(symbol)}`,
                        )
                    }
                    className="mt-6 flex w-full items-center justify-center gap-1.5 rounded-xl bg-green-600 dark:bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-green-700 dark:hover:bg-emerald-500 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 dark:focus:ring-offset-slate-900"
                >
                    More Info
                    <span aria-hidden="true">&rarr;</span>
                </button>
            </div>
        </div>
    );
};

export default SymbolPopup;
